import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";

const inputCls = "w-full border border-line bg-ink px-3 py-2.5 text-sm text-white outline-none transition-colors focus:border-accent";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function MasterPage({ endpoint, title, subtitle, empty, extraField, testidPrefix }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get(endpoint).then((r) => setItems(r.data)).catch(() => toast.error("取得に失敗しました"));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing.id) await api.put(`${endpoint}/${editing.id}`, form);
      else await api.post(endpoint, form);
      toast.success("保存しました");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`「${row.name}」を削除しますか？`)) return;
    try {
      await api.delete(`${endpoint}/${row.id}`);
      toast.success("削除しました");
      load();
    } catch {
      toast.error("削除に失敗しました");
    }
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  return (
    <div data-testid={`admin-${testidPrefix}-page`}>
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-mincho text-2xl font-bold text-white">{title}</h1>
          <p className="mt-1 font-mono text-[10px] tracking-[0.3em] text-gray-500">{subtitle}</p>
        </div>
        <button data-testid={`${testidPrefix}-add-button`} onClick={() => { setForm(empty); setEditing({}); }} className="flex items-center gap-2 bg-accent px-5 py-2.5 font-display text-xs font-bold tracking-[0.15em] text-ink transition-colors hover:bg-accent-hover">
          <Plus className="h-4 w-4" />
          新規追加
        </button>
      </div>

      <div className="mt-8 overflow-x-auto border border-line">
        <table className="w-full min-w-[700px] text-left text-sm">
          <thead>
            <tr className="border-b border-line bg-coal font-mono text-[10px] tracking-[0.2em] text-gray-500">
              <th className="px-4 py-3">名称</th>
              <th className="px-4 py-3">担当者</th>
              <th className="px-4 py-3">連絡先</th>
              <th className="px-4 py-3">状態</th>
              <th className="px-4 py-3">操作</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={row.id} data-testid={`${testidPrefix}-row-${row.id}`} className="border-b border-line bg-panel last:border-b-0">
                <td className="px-4 py-3 text-xs font-bold text-white">{row.name}</td>
                <td className="px-4 py-3 text-xs text-gray-400">{row.contact_person || "—"}</td>
                <td className="px-4 py-3 text-xs text-gray-400">{row.tel || row.email || "—"}</td>
                <td className="px-4 py-3 text-xs">{row.is_active ? <span className="text-green-400">有効</span> : <span className="text-gray-500">無効</span>}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button data-testid={`${testidPrefix}-edit-${row.id}`} onClick={() => { setForm({ ...empty, ...row }); setEditing(row); }} className="border border-line p-2 text-gray-400 transition-colors hover:border-accent hover:text-accent">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button data-testid={`${testidPrefix}-delete-${row.id}`} onClick={() => remove(row)} className="border border-line p-2 text-gray-400 transition-colors hover:border-red-500 hover:text-red-400">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">登録がありません。</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/80 p-4 backdrop-blur-sm">
          <form data-testid={`${testidPrefix}-form`} onSubmit={save} className="max-h-[90vh] w-full max-w-xl space-y-4 overflow-y-auto border border-line bg-coal p-8">
            <div className="flex items-center justify-between">
              <h2 className="font-mincho text-lg font-bold text-white">{editing.id ? "編集" : "新規追加"}</h2>
              <button type="button" data-testid={`${testidPrefix}-form-close`} onClick={() => setEditing(null)} className="text-gray-500 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="名称 *"><input data-testid={`${testidPrefix}-form-name`} required value={form.name} onChange={set("name")} className={inputCls} /></Field>
              <Field label="フリガナ"><input value={form.name_kana} onChange={set("name_kana")} className={inputCls} /></Field>
              <Field label="担当者"><input value={form.contact_person} onChange={set("contact_person")} className={inputCls} /></Field>
              <Field label="電話番号"><input value={form.tel} onChange={set("tel")} className={inputCls} /></Field>
              <Field label="メールアドレス"><input type="email" value={form.email} onChange={set("email")} className={inputCls} /></Field>
              <Field label="郵便番号"><input value={form.postal_code} onChange={set("postal_code")} className={inputCls} /></Field>
            </div>
            <Field label="住所"><input value={form.address} onChange={set("address")} className={inputCls} /></Field>
            {extraField === "qualifications" && (
              <Field label="許可・資格・保険"><textarea rows={2} value={form.qualifications || ""} onChange={set("qualifications")} className={`resize-y ${inputCls}`} /></Field>
            )}
            <Field label="備考"><textarea rows={2} value={form.notes} onChange={set("notes")} className={`resize-y ${inputCls}`} /></Field>
            <label className="flex items-center gap-3 text-sm text-gray-300">
              <input data-testid={`${testidPrefix}-form-active`} type="checkbox" checked={form.is_active} onChange={set("is_active")} className="h-4 w-4 accent-accent" />
              有効
            </label>
            <button data-testid={`${testidPrefix}-form-submit`} type="submit" disabled={saving} className="w-full bg-accent py-3 font-display text-sm font-bold tracking-[0.2em] text-ink transition-colors hover:bg-accent-hover disabled:opacity-50">
              {saving ? "保存中..." : "保存する"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

const CUSTOMER_EMPTY = { name: "", name_kana: "", contact_person: "", tel: "", email: "", postal_code: "", address: "", notes: "", is_active: true };
const PARTNER_EMPTY = { ...CUSTOMER_EMPTY, qualifications: "" };

export function AdminCustomers() {
  return <MasterPage endpoint="/admin/customers" title="顧客・取引先の管理" subtitle="CUSTOMERS" empty={CUSTOMER_EMPTY} testidPrefix="customer" />;
}

export function AdminPartners() {
  return <MasterPage endpoint="/admin/partners" title="協力会社の管理" subtitle="PARTNERS" empty={PARTNER_EMPTY} extraField="qualifications" testidPrefix="partner" />;
}
